import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ProfileFormData } from "@/pages/ProfileCreation";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useState, useEffect, useRef, type ChangeEvent } from "react";
import { DealbreakerQuestionsSection } from "./DealbreakerQuestionsSection";
import { PREFERRED_CONTACT_METHOD, type PreferredContactMethod } from "@/lib/enums";
import {
  formatPhoneForDisplay,
  formatPhoneInput,
  isPlausiblePhone,
  optionalPhoneSchema,
  requiredPhoneSchema,
  PHONE_ERROR_MESSAGE,
} from "@/lib/phone";
import { ReferencesInput } from "./ReferencesInput";
import {
  REQUIRED_REFERENCE_COUNT,
  isValidReference,
  type ProfileReference,
} from "@/lib/requiredFields";

// Single validation source of truth for both signup flows. New signups
// (!isEditingExisting) must provide phone, community connection, and exactly
// REQUIRED_REFERENCE_COUNT valid references. When editing an existing profile
// these stay optional — the backfill prompt (manifest-driven) owns reprompting
// legacy members for missing required fields, not this wizard.
//
// Phone fields validate through the shared util (@/lib/phone) and are
// grandfathered against the value they were LOADED with, so a legacy row in an
// older format can always be re-saved untouched — only a number the member
// actually retypes has to satisfy the current rule.
const createReferenceSchema = (loadedPhones: Set<string>) =>
  z
    .object({
      name: z.string().trim().min(1, "Name is required"),
      relationship: z.string().trim().min(1, "Tell us how they know you"),
      phone: z.string().optional().nullable(),
      email: z.string().optional().nullable(),
    })
    .refine((r) => isValidReference(r), {
      message: "Add a phone or email for this reference",
      path: ["phone"],
    })
    .refine(
      (r) => {
        const phone = r.phone?.trim() ?? "";
        return !phone || loadedPhones.has(phone) || isPlausiblePhone(phone);
      },
      { message: PHONE_ERROR_MESSAGE, path: ["phone"] },
    );

const createSchema = (
  isEditingExisting: boolean,
  isFemale: boolean,
  loaded: ProfileFormData,
) => {
  const referenceSchema = createReferenceSchema(
    new Set(
      (loaded.references ?? [])
        .map((r) => r.phone?.trim() ?? "")
        .filter(Boolean),
    ),
  );

  return z.object({
    email: z.string().email("Invalid email address"),
    phone: isEditingExisting
      ? optionalPhoneSchema({ allow: loaded.phone })
      : requiredPhoneSchema({ allow: loaded.phone }),
    whatsapp: optionalPhoneSchema({ allow: loaded.whatsapp }),
    preferred_contact_method: z.enum([...PREFERRED_CONTACT_METHOD] as [string, ...string[]]),
    wali_name: z.string().optional(),
    wali_email: z.string().optional(),
    wali_phone: optionalPhoneSchema({ allow: loaded.wali_phone }),
    reveal_wali: z.boolean().optional(),
    community_connection: isEditingExisting
      ? z.string().optional()
      : z.string().trim().min(1, "Please tell us your connection to the community"),
    references: isEditingExisting
      ? z.array(referenceSchema).optional()
      : z
          .array(referenceSchema)
          .refine(
            (refs) => refs.filter(isValidReference).length >= REQUIRED_REFERENCE_COUNT,
            `Please provide ${REQUIRED_REFERENCE_COUNT} references`,
          ),
    matchmaker_notes: z.string().max(1000, "Notes must be 1000 characters or less").optional(),
    // Only require policy checkboxes for new profiles
    agree_terms: isEditingExisting ? z.boolean().optional() : z.boolean().refine((val) => val === true, "You must agree to the terms"),
    agree_privacy: isEditingExisting ? z.boolean().optional() : z.boolean().refine((val) => val === true, "You must agree to the privacy policy"),
    understand_review: isEditingExisting ? z.boolean().optional() : z.boolean().refine((val) => val === true, "You must acknowledge profile review"),
    affirm_truthful: isEditingExisting ? z.boolean().optional() : z.boolean().refine((val) => val === true, "You must affirm information is truthful"),
  }).refine(
    (v) => {
      // Brothers never see the wali block, so it can never gate their submit.
      if (!isFemale) return true;
      const usesWali = v.reveal_wali === true || !!v.wali_name?.trim() || !!v.wali_phone?.trim();
      return !usesWali || !!v.wali_email?.trim();
    },
    {
      message: "Your wali's email is required so we can reach him",
      path: ["wali_email"],
    },
  );
};

interface Props {
  data: ProfileFormData;
  onComplete: (data: Partial<ProfileFormData>) => void;
  onBack: () => void;
  onEditStep: (step: number) => void;
  saving: boolean;
  isEditingExisting?: boolean;
  submitLabel?: string;
  loadingLabel?: string;
}

export function Step6ContactReview({ data, onComplete, onBack, onEditStep, saving, isEditingExisting = false, submitLabel = "Complete Profile", loadingLabel = "Submitting..." }: Props) {
  const isFemale = data.gender === "female";
  const [showConfetti, setShowConfetti] = useState(false);
  const [dealbreakerQuestions, setDealbreakerQuestions] = useState(data.dealbreaker_questions || []);
  const [references, setReferences] = useState<ProfileReference[]>(
    data.references && data.references.length > 0
      ? data.references
      : [
          { name: "", relationship: "", phone: "", email: "" },
          { name: "", relationship: "", phone: "", email: "" },
        ],
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createSchema(isEditingExisting, isFemale, data)),
    defaultValues: {
      email: data.email,
      phone: formatPhoneForDisplay(data.phone),
      whatsapp: formatPhoneForDisplay(data.whatsapp),
      preferred_contact_method: (data.preferred_contact_method as PreferredContactMethod) || "Email",
      wali_name: data.wali_name || "",
      wali_email: data.wali_email || "",
      wali_phone: formatPhoneForDisplay(data.wali_phone),
      reveal_wali: data.reveal_wali || false,
      community_connection: data.community_connection || "",
      references: data.references || [],
      matchmaker_notes: data.matchmaker_notes || "",
      agree_terms: data.agree_terms || false,
      agree_privacy: data.agree_privacy || false,
      understand_review: data.understand_review || false,
      affirm_truthful: data.affirm_truthful || false,
    },
  });

  // Keep the RHF "references" field in sync with local references state so the
  // shared zod schema validates it. Only re-validate once a references error
  // is already showing — otherwise every keystroke on step entry would
  // validate the still-incomplete array and flash errors before the member
  // has had a chance to fill it in. `errors.references` is deliberately read
  // from the closure, not listed as a dep: validating on change flips it,
  // and depending on it would re-fire this effect on its own output, looping.
  useEffect(() => {
    setValue("references", references, {
      shouldValidate: !!errors.references,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [references, setValue]);

  const preferredContact = watch("preferred_contact_method");

  // Live phone formatting. register() is spread onto the input first so its
  // name/ref/onBlur still apply; this onChange replaces RHF's so the FORMATTED
  // value is what lands in form state and in the box. Re-validate on change
  // only once that field already has an error showing, so a fixed number
  // clears its error live instead of waiting for the next submit.
  const handlePhoneChange =
    (field: "phone" | "whatsapp" | "wali_phone") =>
    (e: ChangeEvent<HTMLInputElement>) =>
      setValue(field, formatPhoneInput(e.target.value), {
        shouldDirty: true,
        shouldValidate: !!errors[field],
      });

  // RHF reports per-item reference errors as a sparse array; hand them to
  // ReferencesInput so the message lands on the field that caused it.
  const referenceErrors = Array.isArray(errors.references)
    ? (errors.references as Array<Record<string, { message?: string }> | undefined>).map(
        (e) => ({
          name: e?.name?.message,
          relationship: e?.relationship?.message,
          phone: e?.phone?.message,
          email: e?.email?.message,
        }),
      )
    : undefined;


  // Hydrate once on mount / when re-entering this step (especially for
  // checkboxes loaded from localStorage). Do NOT re-apply on every parent
  // re-render — that clobbered in-flight user input (bug B2).
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (hasHydrated.current) return;
    hasHydrated.current = true;
    setValue("email", data.email);
    setValue("phone", formatPhoneForDisplay(data.phone));
    setValue("whatsapp", formatPhoneForDisplay(data.whatsapp));
    setValue("preferred_contact_method", (data.preferred_contact_method as PreferredContactMethod) || "Email");
    setValue("wali_name", data.wali_name || "");
    setValue("wali_email", data.wali_email || "");
    setValue("wali_phone", formatPhoneForDisplay(data.wali_phone));
    setValue("reveal_wali", data.reveal_wali || false);
    setValue("community_connection", data.community_connection || "");
    setValue("matchmaker_notes", data.matchmaker_notes || "");
    setValue("agree_terms", data.agree_terms || false);
    setValue("agree_privacy", data.agree_privacy || false);
    setValue("understand_review", data.understand_review || false);
    setValue("affirm_truthful", data.affirm_truthful || false);
  }, [data, setValue]);

  const onSubmit = (formData: any) => {
    // Save checkbox state and dealbreaker questions before completing
    onComplete({
      ...formData,
      community_connection: formData.community_connection,
      references,
      agree_terms: formData.agree_terms,
      agree_privacy: formData.agree_privacy,
      understand_review: formData.understand_review,
      affirm_truthful: formData.affirm_truthful,
      dealbreaker_questions: dealbreakerQuestions,
    });
  };

  const calculateCompleteness = () => {
    const fields = [
      data.first_name, data.age, data.gender, data.location, data.education_level,
      data.religiosity_level, data.prayer_frequency, data.bio, data.seeking_description,
      data.email
    ];
    const filled = fields.filter(f => f && f !== "").length;
    return Math.round((filled / fields.length) * 100);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Contact Details</h3>
        
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            {...register("email")}
            placeholder="your.email@example.com"
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone number *</Label>
          <Input
            id="phone"
            type="tel"
            autoComplete="tel"
            {...register("phone")}
            onChange={handlePhoneChange("phone")}
            placeholder="(555) 123-4567"
          />
          <p className="text-sm text-muted-foreground">
            US numbers format as you type. For an international number, start with + and the country code.
          </p>
          {errors.phone && (
            <p className="text-sm text-destructive">{errors.phone.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="whatsapp">WhatsApp number (optional)</Label>
          <Input
            id="whatsapp"
            type="tel"
            {...register("whatsapp")}
            onChange={handlePhoneChange("whatsapp")}
            placeholder="(555) 123-4567"
          />
          <p className="text-sm text-muted-foreground">Can be same as phone number</p>
          {errors.whatsapp && (
            <p className="text-sm text-destructive">{errors.whatsapp.message as string}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Preferred contact method *</Label>
          <RadioGroup
            value={preferredContact}
            onValueChange={(value) => setValue("preferred_contact_method", value as PreferredContactMethod)}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Email" id="contact-email" />
              <Label htmlFor="contact-email" className="font-normal cursor-pointer">Email</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Phone" id="contact-phone" />
              <Label htmlFor="contact-phone" className="font-normal cursor-pointer">Phone</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="WhatsApp" id="contact-whatsapp" />
              <Label htmlFor="contact-whatsapp" className="font-normal cursor-pointer">WhatsApp</Label>
            </div>
          </RadioGroup>
          {errors.preferred_contact_method && (
            <p className="text-sm text-destructive">
              {errors.preferred_contact_method.message as string}
            </p>
          )}
        </div>
      </div>

      {isFemale && (
        <>
          <Separator />

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Your Wali (optional)</h3>
              <p className="text-sm text-muted-foreground">
                If you'd like your wali involved, tell us how to reach him. These
                details are never shown to other members unless you turn on the
                option below.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wali_name">Wali's name</Label>
              <Input
                id="wali_name"
                {...register("wali_name")}
                placeholder="e.g. Abdullah Rahman"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wali_email">Wali's email</Label>
              <Input
                id="wali_email"
                type="email"
                {...register("wali_email")}
                placeholder="wali.email@example.com"
              />
              <p className="text-sm text-muted-foreground">
                Required if you fill in anything else here — it's how we notify him.
              </p>
              {errors.wali_email && (
                <p className="text-sm text-destructive">{errors.wali_email.message as string}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="wali_phone">Wali's phone</Label>
              <Input
                id="wali_phone"
                type="tel"
                {...register("wali_phone")}
                onChange={handlePhoneChange("wali_phone")}
                placeholder="(555) 123-4567"
              />
              {errors.wali_phone && (
                <p className="text-sm text-destructive">{errors.wali_phone.message as string}</p>
              )}
            </div>

            <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
              <Label htmlFor="reveal_wali" className="font-normal cursor-pointer">
                Show my wali's contact instead of my own once a match is confirmed
              </Label>
              <Switch
                id="reveal_wali"
                checked={watch("reveal_wali")}
                onCheckedChange={(checked) => setValue("reveal_wali", checked)}
              />
            </div>
          </div>
        </>
      )}

      <Separator />

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Your Connection to the Community</h3>
          <p className="text-sm text-muted-foreground">
            This is the community's matrimonial committee — matchmakers are members
            you may already know. Telling us how you're connected helps us place you.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="community_connection">
            What is your connection to the community? *
          </Label>
          <Textarea
            id="community_connection"
            {...register("community_connection")}
            placeholder="e.g. Which masjid you attend, who you know in the community, how you found us…"
            className="min-h-[100px]"
          />
          {errors.community_connection && (
            <p className="text-sm text-destructive">
              {errors.community_connection.message as string}
            </p>
          )}
        </div>
      </div>

      <Separator />

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">References</h3>
          <p className="text-sm text-muted-foreground">
            Two people who know you well — so our matchmakers can ask around about you
            the traditional way. These details are private: only your matchmaker sees
            them, never other members.
          </p>
        </div>

        <ReferencesInput
          value={references}
          onChange={setReferences}
          errors={referenceErrors}
        />
        {/* Array-level message only — per-reference problems render inline above. */}
        {(errors.references as { message?: string })?.message && (
          <p className="text-sm text-destructive">
            {(errors.references as { message?: string }).message}
          </p>
        )}
      </div>

      <Separator />

      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-foreground">Private Notes to Matchmaker</h3>

        <div className="space-y-2">
          <Label htmlFor="matchmaker_notes">Additional information for matchmaker (optional)</Label>
          <Textarea
            id="matchmaker_notes"
            {...register("matchmaker_notes")}
            placeholder="Any specific preferences, deal-breakers, or context you'd like to share with your matchmaker (visible only to matchmakers, not to potential matches)"
            className="min-h-[120px]"
            maxLength={1000}
          />
          <p className="text-sm text-muted-foreground">
            These notes are private and will only be seen by your matchmaker. They will NOT be visible to your matches.
          </p>
          {errors.matchmaker_notes && (
            <p className="text-sm text-destructive">{errors.matchmaker_notes.message}</p>
          )}
        </div>
      </div>

      <Separator />

      {/* Dealbreaker Questions Section */}
      <DealbreakerQuestionsSection
        questions={dealbreakerQuestions}
        onChange={(questions) => {
          // Just update local state - don't trigger form submission
          setDealbreakerQuestions(questions);
        }}
      />

      {/* Only show Privacy & Consent section for new profiles */}
      {!isEditingExisting && (
        <>
          <Separator />

          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground">Privacy & Consent</h3>

            <div className="space-y-4">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="agree_terms"
                  {...register("agree_terms")}
                  onCheckedChange={(checked) => setValue("agree_terms", checked as boolean)}
                />
                <div className="space-y-1">
                  <Label htmlFor="agree_terms" className="font-normal cursor-pointer">
                    I agree to the{" "}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:no-underline"
                    >
                      Terms of Service
                    </Link>
                    {" "}*
                  </Label>
                  {errors.agree_terms && (
                    <p className="text-sm text-destructive">{errors.agree_terms.message}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="agree_privacy"
                  {...register("agree_privacy")}
                  onCheckedChange={(checked) => setValue("agree_privacy", checked as boolean)}
                />
                <div className="space-y-1">
                  <Label htmlFor="agree_privacy" className="font-normal cursor-pointer">
                    I agree to the{" "}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:no-underline"
                    >
                      Privacy Policy
                    </Link>
                    {" "}*
                  </Label>
                  {errors.agree_privacy && (
                    <p className="text-sm text-destructive">{errors.agree_privacy.message}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="understand_review"
                  {...register("understand_review")}
                  onCheckedChange={(checked) => setValue("understand_review", checked as boolean)}
                />
                <div className="space-y-1">
                  <Label htmlFor="understand_review" className="font-normal cursor-pointer">
                    I understand my profile will be reviewed by a matchmaker *
                  </Label>
                  {errors.understand_review && (
                    <p className="text-sm text-destructive">{errors.understand_review.message}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="affirm_truthful"
                  {...register("affirm_truthful")}
                  onCheckedChange={(checked) => setValue("affirm_truthful", checked as boolean)}
                />
                <div className="space-y-1">
                  <Label htmlFor="affirm_truthful" className="font-normal cursor-pointer">
                    I affirm all information provided is truthful *
                  </Label>
                  {errors.affirm_truthful && (
                    <p className="text-sm text-destructive">{errors.affirm_truthful.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Separator />
        </>
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Profile Review</h3>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="font-medium">{calculateCompleteness()}% Complete</span>
          </div>
        </div>
        
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="basic">
            <div className="flex items-center justify-between">
              <AccordionTrigger className="text-sm flex-1">
                Basic Information
              </AccordionTrigger>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEditStep(0)}
                className="text-xs"
              >
                Edit
              </Button>
            </div>
            <AccordionContent className="text-sm space-y-1">
              <p><strong>Name:</strong> {data.first_name}</p>
              <p><strong>Age:</strong> {data.age}</p>
              <p><strong>Gender:</strong> {data.gender}</p>
              <p><strong>Location:</strong> {data.location}</p>
              {data.height_feet && <p><strong>Height:</strong> {data.height_feet}'{data.height_inches}"</p>}
              <p><strong>Ethnicity:</strong> {data.ethnicity}</p>
              <p><strong>Citizenship:</strong> {data.citizenship_status}</p>
              <p><strong>Marital Status:</strong> {data.marital_status}</p>
              <p><strong>Has Children:</strong> {data.has_children ? `Yes (${data.children_count || 0})` : 'No'}</p>
              <p><strong>Willing to Relocate:</strong> {data.willing_to_relocate ? 'Yes' : 'No'}</p>
              {data.profile_picture_url && <p><strong>Profile Picture:</strong> Uploaded</p>}
              {data.profile_pictures && data.profile_pictures.length > 0 && (
                <p><strong>Additional Pictures:</strong> {data.profile_pictures.length} photo(s)</p>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="religious">
            <div className="flex items-center justify-between">
              <AccordionTrigger className="text-sm flex-1">
                Religious Profile
              </AccordionTrigger>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEditStep(1)}
                className="text-xs"
              >
                Edit
              </Button>
            </div>
            <AccordionContent className="text-sm space-y-1">
              <p><strong>Religiosity Level:</strong> {data.religiosity_level}</p>
              <p><strong>Islam Importance:</strong> {data.importance_of_islam}/10</p>
              <p><strong>Sect:</strong> {data.sect}</p>
              <p><strong>Prayer Frequency:</strong> {data.prayer_frequency}</p>
              <p><strong>Masjid Attendance:</strong> {data.masjid_attendance}</p>
              {data.gender === 'female' && data.wears_hijab_level && (
                <p><strong>Hijab:</strong> {data.wears_hijab_level}</p>
              )}
              {data.gender === 'male' && data.has_beard && (
                <p><strong>Beard:</strong> {data.has_beard}</p>
              )}
              <p><strong>Quran Knowledge:</strong> {data.quran_knowledge}</p>
              <p><strong>Islamic Knowledge:</strong> {data.islamic_knowledge_level}</p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="education">
            <div className="flex items-center justify-between">
              <AccordionTrigger className="text-sm flex-1">
                Education & Career
              </AccordionTrigger>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEditStep(2)}
                className="text-xs"
              >
                Edit
              </Button>
            </div>
            <AccordionContent className="text-sm space-y-1">
              <p><strong>Education:</strong> {data.education_level}</p>
              {data.field_of_study && <p><strong>Field of Study:</strong> {data.field_of_study}</p>}
              <p><strong>Occupation:</strong> {data.occupation}</p>
              <p><strong>Employment Status:</strong> {data.employment_status}</p>
              <p><strong>Financial Independence:</strong> {data.financial_status}/5</p>
              {data.career_description && (
                <p><strong>Career Description:</strong> {data.career_description.substring(0, 100)}{data.career_description.length > 100 ? '...' : ''}</p>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="lifestyle">
            <div className="flex items-center justify-between">
              <AccordionTrigger className="text-sm flex-1">
                Lifestyle & Personality
              </AccordionTrigger>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEditStep(3)}
                className="text-xs"
              >
                Edit
              </Button>
            </div>
            <AccordionContent className="text-sm space-y-1">
              <p><strong>Personality Type:</strong> {data.personality_type}/10 (Introvert-Extrovert)</p>
              {data.personality_description && (
                <p><strong>Personality Description:</strong> {data.personality_description.substring(0, 100)}{data.personality_description.length > 100 ? '...' : ''}</p>
              )}
              <p><strong>Smoking:</strong> {data.smoking}</p>
              {data.dietary_preferences && data.dietary_preferences.length > 0 && (
                <p><strong>Dietary Preferences:</strong> {data.dietary_preferences.join(', ')}</p>
              )}
              {data.exercise_routine && <p><strong>Exercise Routine:</strong> {data.exercise_routine}</p>}
              {data.bio && (
                <p><strong>Bio:</strong> {data.bio.substring(0, 150)}{data.bio.length > 150 ? '...' : ''}</p>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="preferences">
            <div className="flex items-center justify-between">
              <AccordionTrigger className="text-sm flex-1">
                Your Preferences
              </AccordionTrigger>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEditStep(4)}
                className="text-xs"
              >
                Edit
              </Button>
            </div>
            <AccordionContent className="text-sm space-y-1">
              <p><strong>Age Range:</strong> {data.seeking_age_min}-{data.seeking_age_max}</p>
              {(data.seeking_height_min_feet || data.seeking_height_max_feet) && (
                <p><strong>Height Range:</strong> {data.seeking_height_min_feet ? `${data.seeking_height_min_feet}'${data.seeking_height_min_inches || 0}"` : 'Any'} - {data.seeking_height_max_feet ? `${data.seeking_height_max_feet}'${data.seeking_height_max_inches || 0}"` : 'Any'}</p>
              )}
              {data.seeking_location_preference && (
                <p><strong>Location Preference:</strong> {data.seeking_location_preference}</p>
              )}
              <p><strong>Ethnicity:</strong> {data.seeking_ethnicity?.join(", ") || "Not specified"}</p>
              <p><strong>Sect:</strong> {data.seeking_sect?.join(", ") || "Not specified"}</p>
              {data.seeking_religiosity && data.seeking_religiosity.length > 0 && (
                <p><strong>Religiosity Level:</strong> {data.seeking_religiosity.join(", ")}</p>
              )}
              {data.seeking_masjid_attendance_min && (
                <p><strong>Minimum Masjid Attendance:</strong> {data.seeking_masjid_attendance_min}</p>
              )}
              {data.gender === 'male' && data.seeking_hijab_preference && (
                <p><strong>Hijab Preference:</strong> {data.seeking_hijab_preference}</p>
              )}
              {data.seeking_citizenship && data.seeking_citizenship.length > 0 && (
                <p><strong>Citizenship:</strong> {data.seeking_citizenship.join(", ")}</p>
              )}
              <p><strong>Marital Status:</strong> {data.seeking_marital_status?.join(", ") || "Not specified"}</p>
              <p><strong>Open to Children:</strong> {data.seeking_children_ok ? 'Yes' : 'No'}</p>
              <p><strong>Open to Converts:</strong> {data.seeking_convert_ok ? 'Yes' : 'No'}</p>
              <p><strong>Open to Divorced:</strong> {data.seeking_divorced_ok ? 'Yes' : 'No'}</p>
              {(data.seeking_personality_min || data.seeking_personality_max) && (
                <p><strong>Personality Range:</strong> {data.seeking_personality_min || 1} - {data.seeking_personality_max || 10}</p>
              )}
              {data.seeking_financial_min && (
                <p><strong>Minimum Financial Independence:</strong> {data.seeking_financial_min}/5</p>
              )}
              {data.seeking_education && data.seeking_education.length > 0 && (
                <p><strong>Education:</strong> {data.seeking_education.join(", ")}</p>
              )}
              {data.seeking_description && (
                <p><strong>Description:</strong> {data.seeking_description.substring(0, 150)}{data.seeking_description.length > 150 ? '...' : ''}</p>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <div className="flex justify-between gap-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" disabled={saving} className="min-w-32">
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {loadingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}
